import { IFileObject } from 'transfix-ui/components/Dropzone'
import uuid from 'uuid'
import { IFile, IFileCreate } from '../types'

const TEN_MEGABYTES = 10485760

/**
 * Maps the values from MUI-Dropzone into the format that Fleet Planner expects for file upload.
 * @param files
 */
export const mapDropzoneFiles = (files: IFileObject[] = []): IFileCreate[] => {
  return files.map(f => {
    const { data, file } = f || {}
    const { name, size } = file || {}
    return { file: data, fileFileName: name, uuid: uuid(), size }
  })
}

interface IMappedFile extends Partial<Pick<IFile, '_destroy' | 'id' | 'url'>> {}

/**
 * Returns an array with { _destroy: true } for matching item
 */
export function mapRemovedFiles<T extends IMappedFile>(
  attachments: T[] = [],
  removedId: number
): T[] {
  return attachments.map(attachment => {
    if (attachment?.id === removedId) {
      attachment._destroy = true
    }
    return attachment
  })
}

/**
 * Returns an array with { _destroy: true } for matching item
 */
export function mapRemovedFilesByUrl<T extends IMappedFile>(
  attachments: T[] = [],
  removedUrl: string
): T[] {
  return attachments.map(attachment => {
    if (attachment?.url === removedUrl) {
      attachment._destroy = true
    }
    return attachment
  })
}

export const formatFileSizeInMB = (fileSizeInBytes: number) => {
  const oneMBtoKB = 1024
  const fileSizeInMB = fileSizeInBytes / (oneMBtoKB * oneMBtoKB)
  return {
    label: `${fileSizeInMB.toFixed(1)} MB`,
    bytes: fileSizeInBytes,
    megabytes: fileSizeInMB.toFixed(1),
  }
}

export const sumOfFilesSizeInMB = (files: IFileCreate[]) => {
  const fileSizeInBytes = files.reduce((totalSize, { size = 0 }) => totalSize + size, 0)
  return formatFileSizeInMB(fileSizeInBytes)
}

export const documentUploadCaption = (files: IFileCreate[]) => {
  const { bytes = 0 } = sumOfFilesSizeInMB(files) || {}
  const remainingFileSize = TEN_MEGABYTES - bytes
  const { label } = formatFileSizeInMB(remainingFileSize)
  const isFilesSizeLimitExceeded = remainingFileSize < 0

  const remainingFileSizeCaption =
    files.length > 0 ? `${label} Remaining` : 'Maximum combined file size: 10MB'

  const fileSizeCaption = isFilesSizeLimitExceeded
    ? 'Combined file size cannot exceed 10 MB'
    : remainingFileSizeCaption

  return { caption: fileSizeCaption, isFilesSizeLimitExceeded }
}
